import {
  createListenerMiddleware,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  getATECasesApi,
  retrieveCaseStatusForEditCaseAPI,
  sendRequestToOwnerApi,
} from "src/api/ateCasesAPI";
import { IATECase, IATECaseData } from "../interfaces/IATECase";
import { NUMBER_OF_ROWS_PER_PAGE } from "src/resources/constant";
import { SLABreachedStatuses, SLABreachedStatusesAr } from "src/resources/enum";
import {
  getStatusByStatusId,
  getTextValueById,
  toCamel,
  toPascal,
  convertToISO,
  convertDateIFNotMin,
  isCivilDefenseUser,
} from "src/utils/helpers";
import { trackPromise } from "react-promise-tracker";
 
export const getATECases = (forExport = false) => {
  return async (dispatch: any, getState: any) => {
    const { ateCases, lookup } = getState();
    const {
      citiesOptions,
      emiratesOptions,
      allServiceTerritories,
      systems,
      serviceTerritories,
    } = lookup;
 
    const { filterData, gridCurrentPage, numberOfRowsPerPage } = ateCases;
    let { startDate, endDate } = filterData;
 
    const currentPage = gridCurrentPage + 1;
 
    const allCityTerritories = serviceTerritories.map(
      (item: any) => item.territoryId
    );
    let territories = filterData.territoryId;
    if (filterData.cityId && (!territories || territories.length === 0)) {
      territories = allCityTerritories;
    }
    const data = {
      ...toPascal({
        ...filterData,
        startDate: convertToISO(startDate),
        endDate: convertToISO(endDate),
      }),
      TerritoryId: isCivilDefenseUser() ? territories : filterData.territoryId,
      PageNumber: currentPage,
      PageCount: numberOfRowsPerPage,
     
 
    };
    if (forExport) {
      data.PageNumber = 1;
      data.PageCount = numberOfRowsPerPage * currentPage;
    }
 
    await trackPromise(
      getATECasesApi(data).then((response) => {
        const data = toCamel(response?.data?.CaseInfo || []);
        const totalRecords = response.data?.TotalRowsCount || 0;
        const mappedData = data.map((rec: IATECaseData) => {
          return {
            ...rec,
            city: getTextValueById(rec.cityId, citiesOptions, "cityId"),
            emirate: getTextValueById(
              rec.emirateId,
              emiratesOptions,
              "emirateId"
            ),
            systemService: getTextValueById(
              rec.systemServiceId,
              systems,
              "systemServiceId"
            ),
            territory: getTextValueById(
              rec.serviceTerritoryId,
              allServiceTerritories,
              "territoryId"
            ),
          };
        });
        if (forExport) {
          dispatch(setExportData(mappedData));
        } else {
          dispatch(setATECases({ data: mappedData, totalRecords }));
        }
      })
    );
  };
};
 
export const sendRequestToOwner = () => {
  return async (dispatch: any, getState: any) => {
    const { ateCases } = getState();
    const { requestReferenceNo, typeOfApproval, description } =
      ateCases.ateCaseSendRequestToOwnerPopupData;
    sendRequestToOwnerApi().then((response) => {
      dispatch(
        setCaseStatus({ requestReferenceNo, typeOfApproval, description })
      );
    });
  };
};
 
export const retrieveCaseStatusForEdit = () => {
  return async (dispatch: any, getState: any) => {
    await retrieveCaseStatusForEditCaseAPI().then((response) => {
      dispatch(
        setCaseStatusForEdit(toCamel(response.data.OptionSetItems)) as any
      );
    });
  };
};
 
const initialState: IATECase = {
  ateCasesData: [],
 
  totalRowsCount: 0,
  gridCurrentPage: 0,
  numberOfRowsPerPage: NUMBER_OF_ROWS_PER_PAGE,
 
  isATECasesDetailsPopupOpen: false,
  isATECaseSendRequestToOwnerPopupOpen: false,
  ateCasesDetailsPopupData: {
    caseId: 0,
    status: "",
    statusReason: "",
  },
  ateCaseSendRequestToOwnerPopupData: {
    requestReferenceNo: 0,
    typeOfApproval: "",
    description: "",
  },
  ateCaseSearchText: "",
  filterData: {
    propertyId: "",
    sLABreached: null,
    systemServiceId: "",
    territoryId: "",
    cityId: "",
    status: "",
    startDate: null,
    endDate: null,
    managerSupervisorName: "",
    company: "",
    sortColumn: "createdOn",
    isAscending: true,
    showHistoricalData: false,
    PRPNumber:"",
    propertyTradeLicense:"",
    companyTradeLicenseNumber:"",
  },
  exportData: [],
  caseStatusForEdit: [],
};
 
const ateCasesSlice = createSlice({
  name: "ateCases",
  initialState,
  reducers: {
    setATECases: (state, action) => {
      state.ateCasesData = action.payload.data;
      state.totalRowsCount = action.payload.totalRecords;
    },
    setCaseStatus: (state, action) => {
      const caseId = action.payload.caseId;
      const status = action.payload.status;
      const statusReason = action.payload.statusReason;
      const ateCasesData = state.ateCasesData.map((ateCase) => {
        if (ateCase.ticketNumber === caseId) {
          return {
            ...ateCase,
            status: status,
            statusReason: statusReason,
          };
        }
        return ateCase;
      });
      state.ateCasesData = ateCasesData;
    },
    setIsATECasesDetailsPopupOpen: (state, action) => {
      state.isATECasesDetailsPopupOpen = action.payload;
    },
    setIsATECaseSendRequestToOwnerPopupOpen: (state, action) => {
      state.isATECaseSendRequestToOwnerPopupOpen = action.payload;
    },
    setATECasePopupData: (
      state,
      action: {
        payload: { field: string; value: string | number };
        type: string;
      }
    ): void => {
      state.ateCasesDetailsPopupData = {
        ...state.ateCasesDetailsPopupData,
        [action.payload.field]: action.payload.value,
      };
    },
    setATECaseSendRequestToOwnerPopupData: (
      state,
      action: {
        payload: { field: string; value: string | number };
        type: string;
      }
    ): void => {
      state.ateCaseSendRequestToOwnerPopupData = {
        ...state.ateCaseSendRequestToOwnerPopupData,
        [action.payload.field]: action.payload.value,
      };
    },
    clearATECasePopupData: (state) => {
      state.ateCasesDetailsPopupData = initialState.ateCasesDetailsPopupData;
    },
    clearATECaseSendRequestToOwnerPopupData: (state) => {
      state.ateCaseSendRequestToOwnerPopupData =
        initialState.ateCaseSendRequestToOwnerPopupData;
    },
    setATECaseSearchText: (state, action) => {
      state.ateCaseSearchText = action.payload;
    },
    setFilterDataField: (state, action) => {
      state.filterData = {
        ...state.filterData,
        [action.payload.field]: action.payload.value,
      };
    },
    resetFilterData: (state) => {
      state.filterData = initialState.filterData;
      state.gridCurrentPage = initialState.gridCurrentPage;
    },
    setGridCurrentPage: (state, action) => {
      state.gridCurrentPage = action.payload;
    },
    setNumberOfRowsPerPage: (state, action) => {
      state.numberOfRowsPerPage = action.payload;
    },
    setExportData: (state, action) => {
      state.exportData = action.payload;
    },
    setCaseStatusForEdit: (state, action) => {
      state.caseStatusForEdit = action.payload;
    },
  },
});
 
export const {
  setATECases,
  setCaseStatus,
  setIsATECasesDetailsPopupOpen,
  setIsATECaseSendRequestToOwnerPopupOpen,
  setATECasePopupData,
  setATECaseSendRequestToOwnerPopupData,
  clearATECasePopupData,
  clearATECaseSendRequestToOwnerPopupData,
  setATECaseSearchText,
  setFilterDataField,
  resetFilterData,
  setGridCurrentPage,
  setNumberOfRowsPerPage,
  setExportData,
  setCaseStatusForEdit
} = ateCasesSlice.actions;
 
export default ateCasesSlice.reducer;
 
const makeFilterAppointmentsSelector = (
  dataSourceSelector: (state: any) => any
) =>
  createSelector(
    dataSourceSelector,
    (state: any) => state.ateCases.ateCaseSearchText,
    (state: any) => state.lookup.statusOptions,
    (state: any) => state.lookup.onHoldReasons,
    (ateCasesData, ateCaseSearchText, statuses, onHoldReasons) => {
      return getATECasesFilteredData(
        ateCasesData,
        ateCaseSearchText,
        statuses,
        onHoldReasons
      );
    }
  );
 
export const selectATECases = makeFilterAppointmentsSelector(
  (state: any) => state.ateCases.ateCasesData
);
 
export const selectATECasesForExport = makeFilterAppointmentsSelector(
  (state: any) => state.ateCases.exportData
);
 
const getATECasesListenerMiddleware = createListenerMiddleware();
 
getATECasesListenerMiddleware.startListening({
  actionCreator: setGridCurrentPage,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(getATECases());
  },
});
 
export { getATECasesListenerMiddleware };
function getATECasesFilteredData(
  ateCasesData: any,
  ateCaseSearchText: any,
  statuses: any,
  onHoldReasons: any
): any {
  if (!ateCasesData) return [];
  return ateCasesData
    ?.map((rec: IATECaseData) => {
      const onHoldReason = onHoldReasons?.find(
        (x: { id: number }) => x.id === rec.onHoldReasonId
      );
      const status = getStatusByStatusId(statuses, rec?.statusCodeId);
      return {
        ...rec,
        createdOn: convertDateIFNotMin(rec.createdOn),
        sLABreached: rec.sLABreached
          ? SLABreachedStatuses.Exceeded
          : SLABreachedStatuses.OnTrack,
        sLABreachedAr: rec.sLABreached
          ? SLABreachedStatusesAr.Exceeded
          : SLABreachedStatusesAr.OnTrack,
        statusAr: status?.arabicName,
        status: status?.englishName,
        statusEn: status?.englishName,
        caseId: rec.ticketNumber,
        resolutionDate: convertDateIFNotMin(rec.resolutionDate),
        onHoldReasonEn: onHoldReason?.englishName,
        onHoldReasonAr: onHoldReason?.arabicName,
      };
    })
    .filter((ateCases: IATECaseData) => {
      return Object.values(ateCases).some((field) => {
        return field
          ?.toString()
          ?.toLowerCase()
          ?.includes(ateCaseSearchText.toLowerCase());
      });
    });
}